import React, {
    useState,
    useRef,
    ReactNode,
    useEffect,
    useCallback
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import { AtlassianIcon } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { WACAvatar } from "@/components/global-nav/user-profile-new/src/components/avatar";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    LogoV3 as Logo,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import SearchAndProfile from "../nav-search-and-profile";
import SearchBar from "../search-bar";

import { CloseIcon } from "./icons/close";
import { MenuIcon } from "./icons/menu";
import { DropdownType } from "./mobile-nav";

const mobileLogoCss = css({
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const tabletLogoCss = css({
    display: "none",
    [media.above.sm]: {
        display: "flex",
        alignItems: "center"
    }
});

const productPageLogoCss = css({
    gap: "25px"
});

const logoCss = css({
    height: "21px"
});

const backButtonCss = css({
    fontSize: "0.875rem",
    color: token("color.text.selected"),
    display: "flex",
    alignItems: "center"
});

const profileAndMenuButtonCss = css({
    display: "flex",
    gap: token("space.200"),
    alignContent: "center"
});

const mobileNavUserProfileCss = css({
    display: "none",
    [media.above.sm]: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center"
    }
});

const mobileIconContainerCss = css({
    height: "36px",
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const mobileDropdownMenuCss = css({
    display: "none",

    [media.above.sm]: {
        opacity: 1,
        inset: "0px 0px auto auto",
        insetBlockStart: "100%",
        paddingBlockEnd: token("space.800"),
        minWidth: "380px",
        width: "max-content",
        position: "absolute",
        height: "calc(100vh - 65px)",
        transform: "translate3d(-92%, 56px, 0px)",
        boxSizing: "border-box",
        overflow: "auto",
        borderRadius: "20px",
        borderBlockStart: "10px solid transparent",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)",
        order: 100,
        backgroundColor: wacToken("color.neutral.light0"),
        insetInlineStart: 0,
        transition: "width 200ms ease",
        zIndex: 10,
        animation: "fadein 0.25s 1"
    }
});

const tabletIconContainerCss = css({
    display: "none",

    [media.above.sm]: {
        display: "flex",
        alignItems: "center",
        position: "relative"
    }
});

const profileCss = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    [media.above.sm]: {
        display: "none"
    }
});

type MobileNavTopProps = {
    productKey: ProductKeyTypes;
    isLoggedIn: boolean;
    userName: string;
    picture: string;
    handleClickAvatar: () => void;
    showMenu: boolean;
    handleToggleMenu: (showMenu: boolean) => void;
    menuPage: DropdownType | string;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    handleFirstOpen: (firstOpen: boolean) => void;
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    isOpenSearch: boolean;
    isShowSearchButton?: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    tabletDropdownMenu: ReactNode;
    isProductPage?: boolean;
    isShowTryNowLink?: boolean;
    navigationTrynowCtaText?: string;
    navigationTrynowCtaUrl?: string;
    navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
    navigationTrynowCtaButtonVariant?: "primary" | "secondary";
    isShowAppSwitcher?: boolean;
    isShowUserProfile?: boolean;
    isShowTabletDropdown?: boolean;
    navType?: "globalNav" | "productNav";
};

const DefaultAtlassianLogo = (
    <Logo
        brand="atlassian"
        type="logo"
        appearance="brand"
        height={20}
        width={156}
    />
);

export const MobileNavTop = ({
    productKey,
    isLoggedIn,
    userName,
    picture,
    handleClickAvatar,
    showMenu,
    handleToggleMenu,
    menuPage,
    handleMenuPage,
    handleFirstOpen,
    mobileLogo = DefaultAtlassianLogo,
    mobileLogoAriaLabel = "Atlassian logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "globalNavWacV2",
    isShowSearchButton = true,
    isOpenSearch,
    setIsOpenSearch,
    tabletDropdownMenu,
    isProductPage = false,
    isShowTryNowLink = false,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl,
    navigationTrynowCtaOnClick,
    navigationTrynowCtaButtonVariant = "primary",
    isShowAppSwitcher = true,
    isShowUserProfile = true,
    isShowTabletDropdown = true,
    navType = "globalNav"
}: // eslint-disable-next-line complexity
MobileNavTopProps) => {
    const intl = useIntl();
    const searchAndProfileRef = useRef<HTMLDivElement>(null);
    const atlasUrl = useLocalizedHref("/");
    const [mobileDropdownMenu, setMobileDropdownMenu] =
        useState<HTMLElement | null>(null);

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            const path = event.composedPath && event.composedPath();
            if (mobileDropdownMenu) {
                const style = window.getComputedStyle(mobileDropdownMenu);
                const insideTabletMobileDropdownMenu =
                    Array.prototype.includes.call(
                        Array.prototype.map.call(path, (el: { id: string }) => {
                            return (
                                el.id ===
                                "nav-tablet-mobile-dropdown-menu-button"
                            );
                        }),
                        true
                    );

                if (
                    !insideTabletMobileDropdownMenu &&
                    style.display !== "none"
                ) {
                    handleToggleMenu(false);
                    handleMenuPage("default");
                    handleFirstOpen(false);
                }
            }
        },
        [mobileDropdownMenu, handleToggleMenu, handleMenuPage, handleFirstOpen]
    );

    const handleKeydown = useCallback(
        (event: KeyboardEvent) => {
            if (event.code === "Escape" && showMenu) {
                handleToggleMenu(false);
                handleMenuPage("default");
                handleFirstOpen(false);
            }
        },
        [handleToggleMenu, handleMenuPage, handleFirstOpen, showMenu]
    );

    useEffect(() => {
        const element = document.getElementById(
            "nav-tablet-mobile-dropdown-menu-button"
        );
        setMobileDropdownMenu(element);
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeydown);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [handleClickOutside, handleKeydown]);

    return (
        <>
            <div css={mobileLogoCss} className="mobile-nav-company-logo">
                {menuPage === "default" ||
                !showMenu ||
                menuPage === "search" ||
                menuPage === "resources" ||
                menuPage === "product-everyone" ||
                menuPage === "product-developer" ||
                menuPage === "product-product-managers" ||
                menuPage === "product-it-professionals" ||
                menuPage === "product-business-teams" ||
                menuPage === "product-leadership-teams" ||
                menuPage === "solutions" ||
                menuPage === "why-atlassian" ? (
                    <a
                        css={logoCss}
                        href={mobileLogoHref}
                        aria-label={mobileLogoAriaLabel}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container={mobileLogoDataEventContainer}
                        data-product-key={productKey}
                    >
                        {mobileLogo}
                    </a>
                ) : (
                    <button
                        style={{ all: "unset", width: "100%" }}
                        onClick={(e) => {
                            e.preventDefault();
                            handleMenuPage("default");
                            handleFirstOpen(false);
                        }}
                    >
                        <div css={backButtonCss}>
                            <ChevronLeftIcon label="" spacing="spacious" />
                            <span>
                                {intl.formatMessage({
                                    id: "globalnavv2.menu-back",
                                    defaultMessage: "Back"
                                })}
                            </span>
                        </div>
                    </button>
                )}
            </div>
            <div
                css={[tabletLogoCss, isProductPage ? productPageLogoCss : ""]}
                className="tablet-nav-company-logo"
            >
                {isProductPage && (
                    <div
                        className="productpage-subnav-atlas-icon"
                        css={css({
                            height: "68px",
                            width: "68px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: wacToken("color.extended.blue800")
                        })}
                    >
                        <a
                            className="navigation-bar-atlas-icon"
                            css={css({
                                height: "56px",
                                width: "56px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            })}
                            href={atlasUrl}
                            aria-label={intl.formatMessage({
                                id: "productPageNav.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="productPageSubNav"
                            data-product-key="home"
                        >
                            <AtlassianIcon size="large" appearance="inverse" />
                        </a>
                    </div>
                )}
                <a
                    css={logoCss}
                    href={mobileLogoHref}
                    aria-label={mobileLogoAriaLabel}
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-event-container={mobileLogoDataEventContainer}
                    data-product-key={productKey}
                >
                    {mobileLogo}
                </a>
            </div>
            {isOpenSearch ? (
                <SearchBar
                    isOpenSearch={isOpenSearch}
                    setIsOpenSearch={setIsOpenSearch}
                    isMobile={true}
                />
            ) : (
                <div css={profileAndMenuButtonCss}>
                    <div
                        className="mobile-nav-user-profile"
                        css={mobileNavUserProfileCss}
                    >
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                            navigationTrynowCtaText={navigationTrynowCtaText}
                            navigationTrynowCtaUrl={navigationTrynowCtaUrl}
                            navigationTrynowCtaOnClick={
                                navigationTrynowCtaOnClick
                            }
                            navigationTrynowCtaButtonVariant={
                                navigationTrynowCtaButtonVariant
                            }
                            setIsOpenSearch={setIsOpenSearch}
                            searchAndProfileRef={searchAndProfileRef}
                            isDisplayDivider={false}
                            isShowAppSwitcher={isShowAppSwitcher}
                            isShowUserProfile={isShowUserProfile}
                            navType={navType}
                        />
                        {isShowTabletDropdown && (
                            <div
                                className="navigation-switcher-divider"
                                css={css({
                                    borderInlineStart: `1px solid ${wacToken(
                                        "color.neutral.light300"
                                    )}`,
                                    width: "1px",
                                    height: "24px",
                                    marginInlineStart: token("space.100"),
                                    marginInlineEnd: token("space.100")
                                })}
                            />
                        )}
                    </div>
                    {isShowUserProfile && isLoggedIn && userName && (
                        <div
                            className="mobile-nav-profile-avatar"
                            css={profileCss}
                        >
                            <button
                                style={{ all: "unset" }}
                                aria-label={intl.formatMessage({
                                    id: "globalnavv2.menu-icon.toggle",
                                    defaultMessage: "Toggle Menu"
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleClickAvatar();
                                }}
                            >
                                <WACAvatar
                                    name={userName}
                                    src={picture}
                                    size={"medium"}
                                />
                            </button>
                        </div>
                    )}
                    {/* Mobile button */}
                    <div css={mobileIconContainerCss}>
                        <button
                            style={{ all: "unset" }}
                            aria-label={intl.formatMessage({
                                id: "globalnavv2.menu-icon.toggle",
                                defaultMessage: "Toggle Menu"
                            })}
                            className="globalnavV2ToggleMenu"
                            onClick={(e) => {
                                e.preventDefault();
                                handleToggleMenu(!showMenu);
                                handleMenuPage("default");
                                handleFirstOpen(!showMenu);
                            }}
                        >
                            {showMenu ? (
                                <CloseIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.close",
                                        defaultMessage: "Close Menu"
                                    })}
                                />
                            ) : (
                                <MenuIcon
                                    label={intl.formatMessage({
                                        id: "globalnavv2.menu-icon.open",
                                        defaultMessage: "Open Menu"
                                    })}
                                />
                            )}
                        </button>
                    </div>
                    {/* Tablet button with dropdown */}
                    {isShowTabletDropdown && (
                        <div
                            css={tabletIconContainerCss}
                            id="nav-tablet-mobile-dropdown-menu-button"
                        >
                            <button
                                className="mobile-dropdown-menu-button"
                                style={{
                                    all: "unset",
                                    width: "35px",
                                    height: "35px"
                                }}
                                aria-label={intl.formatMessage({
                                    id: "globalnavv2.menu-icon.toggle",
                                    defaultMessage: "Toggle Menu"
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleToggleMenu(!showMenu);
                                    handleMenuPage("default");
                                    handleFirstOpen(!showMenu);
                                }}
                            >
                                {showMenu ? (
                                    <CloseIcon
                                        label={intl.formatMessage({
                                            id: "globalnavv2.menu-icon.close",
                                            defaultMessage: "Close Menu"
                                        })}
                                    />
                                ) : (
                                    <MenuIcon
                                        label={intl.formatMessage({
                                            id: "globalnavv2.menu-icon.open",
                                            defaultMessage: "Open Menu"
                                        })}
                                    />
                                )}
                            </button>
                            <div
                                className="mobile-dropdown-menu"
                                css={[
                                    mobileDropdownMenuCss,
                                    showMenu &&
                                        css({
                                            [media.above.sm]: {
                                                display: "block"
                                            }
                                        })
                                ]}
                            >
                                {tabletDropdownMenu}
                            </div>
                        </div>
                    )}
                    {showMenu && (
                        <div
                            className="overlay"
                            css={css({
                                position: "fixed",
                                display: "block",
                                width: "100%",
                                height: "100%",
                                insetBlockStart: "0",
                                insetInlineStart: "0",
                                insetInlineEnd: "0",
                                insetBlockEnd: "0",
                                // eslint-disable-next-line local/ensure-wac-token-usage
                                backgroundColor: "rgba(255,255,255,0.7)",
                                zIndex: "-1"
                            })}
                        />
                    )}
                </div>
            )}
        </>
    );
};
